import { create } from "zustand";
import { apiClient } from "../api/client";

// The founder's run-end notification preferences (0027 P6): an in-memory store
// hydrated from /me on app boot (useNotificationSync); each toggle sets the
// store at once and persists through PATCH /api/v1/me/preferences, reverting on
// failure.

export type NotificationPrefs = {
  notificationsOnComplete: boolean;
  soundOnComplete: boolean;
};

interface NotificationPrefsState extends NotificationPrefs {
  /** Replace the entire prefs object (used when /me hydration arrives). */
  hydrate: (prefs: Partial<NotificationPrefs>) => void;
  /** Toggle "notify me when a run finishes" (browser Notification). */
  setNotificationsOnComplete: (v: boolean) => void;
  /** Toggle the completion sound cue. */
  setSoundOnComplete: (v: boolean) => void;
}

const DEFAULT_PREFS: NotificationPrefs = {
  notificationsOnComplete: false,
  soundOnComplete: false,
};

export const useNotificationPrefs = create<NotificationPrefsState>((set, get) => ({
  ...DEFAULT_PREFS,
  hydrate: (prefs) => {
    set({
      notificationsOnComplete:
        prefs.notificationsOnComplete ?? DEFAULT_PREFS.notificationsOnComplete,
      soundOnComplete: prefs.soundOnComplete ?? DEFAULT_PREFS.soundOnComplete,
    });
  },
  // Optimistic local set + persist to the server (0027 P6). Hydrated back
  // from GET /me on boot via useNotificationSync. PATCH is best-effort: on
  // failure we log + revert the local value so the toggle reflects reality.
  setNotificationsOnComplete: (v) => {
    const prev = get().notificationsOnComplete;
    set({ notificationsOnComplete: v });
    apiClient
      .PATCH("/api/v1/me/preferences", {
        params: { header: { "X-Requested-With": "xhr" } },
        body: { notifications_on_complete: v },
      })
      .then(({ error }) => {
        if (error) {
          console.warn("notifications_on_complete PATCH failed; reverted", error);
          set({ notificationsOnComplete: prev });
        }
      })
      .catch((err) => {
        console.warn("notifications_on_complete PATCH failed; reverted", err);
        set({ notificationsOnComplete: prev });
      });
  },
  setSoundOnComplete: (v) => {
    const prev = get().soundOnComplete;
    set({ soundOnComplete: v });
    apiClient
      .PATCH("/api/v1/me/preferences", {
        params: { header: { "X-Requested-With": "xhr" } },
        body: { sound_on_complete: v },
      })
      .then(({ error }) => {
        if (error) {
          console.warn("sound_on_complete PATCH failed; reverted", error);
          set({ soundOnComplete: prev });
        }
      })
      .catch((err) => {
        console.warn("sound_on_complete PATCH failed; reverted", err);
        set({ soundOnComplete: prev });
      });
  },
}));
